import clsx from "clsx";
import {
  add,
  compareAsc,
  compareDesc,
  eachDayOfInterval,
  endOfMonth,
  format,
  parse,
  set,
  startOfMonth,
} from "date-fns";
import { Button, IButtonOptions } from "devextreme-react/button";
import { Calendar } from "devextreme-react/calendar";
import { CheckBox } from "devextreme-react/check-box";
import { DateBox } from "devextreme-react/date-box";
import { Popup, ToolbarItem } from "devextreme-react/popup";
import { SelectBox } from "devextreme-react/select-box";
import { TagBox } from "devextreme-react/tag-box";
import { TextArea } from "devextreme-react/text-area";
import { confirm } from "devextreme/ui/dialog";
import notify from "../../lib/notify";
import { produce } from "immer";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createOrUpdatePresence,
  createPresencePosition,
  rejectPresencePositions,
  updatePresencePosition,
} from "../../pages/presence/api/Presence";
import { Entrance, PresenceEntry } from "../../pages/presence/types/Presence";
import { AbsenceHourRange } from "../../pages/presence/utils/absenceHelpers";
import { useAuth } from "../../contexts/auth";
import {
  PresenceAvailableEmployee,
  PresenceAvailableProject,
  RoleEnum,
} from "../../types";
import { useScreenSize } from "../../utils/media-query";
import { areTimeRangesNonOverlapping } from "../../utils/overlaps";

type PresenceReportingPopupProps = {
  availableEmployees: PresenceAvailableEmployee[];
  availableProjects: PresenceAvailableProject[];
  companyId: number;
  employeeId: number;
  entry: PresenceEntry;
  employeeEntries?: PresenceEntry[];
  canDeleteFullyConfirmed?: boolean;
  absenceHourRange?: AbsenceHourRange | null;
  onClose: (presenceHasBeenCreatedOrUpdated: boolean) => void;
};

export default function PresenceReportingPopup({
  availableEmployees,
  availableProjects,
  companyId,
  employeeId,
  entry,
  employeeEntries,
  canDeleteFullyConfirmed = false,
  absenceHourRange = null,
  onClose,
}: PresenceReportingPopupProps) {
  const date = parse(entry.date, "yyyy-MM-dd", new Date());
  const minDate = startOfMonth(date);
  const maxDate = endOfMonth(date);

  const { t } = useTranslation();
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;
  const { user } = useAuth();

  const roles = user?.roles ?? [];
  const isKadryPlus =
    roles.includes(RoleEnum.KadrySpolki) ||
    roles.includes(RoleEnum.AdministratorSpolki) ||
    roles.includes(RoleEnum.AdministratorGlowny);
  const isMajster = roles.includes(RoleEnum.Majster);

  // Czy bieżący użytkownik może modyfikować/usunąć daną pozycję. Spójne z macierzą na backendzie
  // (CanModifyPosition): zsynchronizowana lub z Optimy -> nie; kadry+ -> każda (Confirmed<=2);
  // kierownik/manager -> tylko własna, Confirmed<=1; majster -> tylko własna, Confirmed==0.
  const canModifyEntrance = useCallback(
    (e: Entrance): boolean => {
      if (e.synch === true) return false;
      if (e.confirmed === null || e.confirmed === undefined) return false;
      if (isKadryPlus) return true;
      const maxConfirmed = isMajster ? 0 : 1;
      const owns =
        e.managerId === user?.employeeId &&
        e.managerCompanyId === user?.companyId;
      return owns && e.confirmed <= maxConfirmed;
    },
    [isKadryPlus, isMajster, user?.employeeId, user?.companyId],
  );

  // Edycja pozycji (spójne z backendem CanModifyPosition): zsynchronizowana z Optimą -> nikt.
  // Kadry+ -> dowolna, dopóki niezsynchronizowana (także zatwierdzona, o ile nie w Optimie).
  // Kierownik/manager -> tylko własna niezatwierdzona (Confirmed<=1); majster -> tylko własna Confirmed==0.
  const canEditEntrance = useCallback(
    (e: Entrance): boolean => {
      if (e.synch === true) return false;
      if (e.confirmed === null || e.confirmed === undefined) return false;
      if (isKadryPlus) return true;
      if (e.confirmed >= 2) return false;
      const maxConfirmed = isMajster ? 0 : 1;
      const owns =
        e.managerId === user?.employeeId &&
        e.managerCompanyId === user?.companyId;
      return owns && e.confirmed <= maxConfirmed;
    },
    [isKadryPlus, isMajster, user?.employeeId, user?.companyId],
  );

  const [isLoading, setIsLoading] = useState(false);
  const isReadOnly = entry.presence !== null;

  // Dopisywanie pozycji do JUŻ istniejącej obecności (append) bez nadpisywania cudzych/zsynchronizowanych.
  // Dozwolone tylko dla obecności z aplikacji (confirmed != null) i dnia jeszcze niezsynchronizowanego
  // z Optimą - inaczej dopisana pozycja i tak nie trafiłaby do Optimy (synchronizator wypycha dzień
  // atomowo i po synchronizacji do niego nie wraca).
  const dayHasSyncedPosition =
    entry.presence?.entrances.some((e) => e.synch === true) ?? false;
  const isOptimaOriginPresence =
    entry.presence !== null && entry.presence.confirmed === null;
  const canAddToExistingPresence =
    entry.presence !== null && !isOptimaOriginPresence && !dayHasSyncedPosition;

  const [addMode, setAddMode] = useState(false);
  const [newPositions, setNewPositions] = useState<
    { fromTime: Date; toTime: Date; projectId: string; note: string }[]
  >([]);
  const [formData, setFormData] = useState(() => {
    const date = parse(entry.date, "yyyy-MM-dd", new Date());

    let positions: {
      id: number | null;
      fromTime: Date;
      toTime: Date;
      projectId: string;
      note: string;
      editable: boolean;
      original: {
        fromTime: Date;
        toTime: Date;
        projectId: string;
        note: string;
      } | null;
    }[] = [];

    if (entry.presence !== null) {
      positions = entry.presence.entrances.map((e) => {
        const fromTime = parse(e.from, "HH:mm:ss", new Date());
        const toTime = parse(e.to, "HH:mm:ss", new Date());
        const projectId = e.projectId?.toString() ?? "";
        return {
          id: e.id,
          fromTime,
          toTime,
          projectId,
          note: e.note,
          editable: canEditEntrance(e),
          original: { fromTime, toTime, projectId, note: e.note },
        };
      });
    } else {
      // jeżeli ktoś chce wpisać obecność na dzień wolny, to nie wczytuję planu pracy tylko ustawiam na stałe 7:00-15:00
      if (entry.workPlan?.dayType === 2 || entry.workPlan?.dayType === 3) {
        const fromTime = set(parse(entry.date, "yyyy-MM-dd", new Date()), {
          hours: 7,
          minutes: 0,
          seconds: 0,
          milliseconds: 0,
        });

        const toTime = set(parse(entry.date, "yyyy-MM-dd", new Date()), {
          hours: 15,
          minutes: 0,
          seconds: 0,
          milliseconds: 0,
        });

        positions = [
          {
            id: null,
            fromTime,
            toTime,
            projectId: "",
            note: "",
            editable: true,
            original: null,
          },
        ];
      } else {
        positions =
          entry.workPlan?.entrances.map((e) => ({
            id: null,
            fromTime: parse(e.from, "HH:mm:ss", new Date()),
            toTime: parse(e.to, "HH:mm:ss", new Date()),
            projectId: "",
            note: e.note,
            editable: true,
            original: null,
          })) ?? [];
      }
    }

    return {
      employeeIds: [`${companyId}-${employeeId}`],
      datesRange: [date, date],
      fromDate: date,
      toDate: date,
      positions: positions,
      skipWeekendsAndHolidays: true,
    };
  });
  const [onlyWeekendDays, setOnlyWeekendDays] = useState(false);

  const handleAddPositionClick = useCallback(() => {
    const previousPosition = formData.positions[formData.positions.length - 1];
    if (previousPosition) {
      const startDate =
        formData.positions[formData.positions.length - 1].toTime;
      const endDate = add(startDate, { hours: 1 });

      setFormData(
        produce(formData, (draft) => {
          draft.positions.push({
            id: null,
            projectId: "",
            fromTime: startDate,
            toTime: endDate,
            note: "",
            editable: true,
            original: null,
          });
        }),
      );
    } else {
      const fromTime = set(parse(entry.date, "yyyy-MM-dd", new Date()), {
        hours: 7,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      });

      const toTime = set(parse(entry.date, "yyyy-MM-dd", new Date()), {
        hours: 15,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      });

      setFormData(
        produce(formData, (draft) => {
          draft.positions.push({
            id: null,
            projectId: "",
            fromTime: fromTime,
            toTime: toTime,
            note: "",
            editable: true,
            original: null,
          });
        }),
      );
    }
  }, [formData]);

  const handleSubmit = useCallback(async () => {
    if (formData.employeeIds.length === 0) {
      notify(
        t("presencePopup.atLeastOneEmployeeMustBeSelected"),
        "warning",
        5000,
      );
    } else if (compareAsc(formData.fromDate, formData.toDate) > 0) {
      notify(
        t("presencePopup.startDateMustBeLessThanOrEqualToEndDate"),
        "warning",
        5000,
      );
    } else if (formData.positions.length === 0) {
      notify(
        t("presencePopup.presenceMustHaveAtLeastOneItem"),
        "warning",
        5000,
      );
    } else if (formData.positions.some((p) => p.projectId === "")) {
      notify(
        t("presencePopup.eachItemMustHaveProjectSelected"),
        "warning",
        5000,
      );
    } else if (
      formData.positions.some((p) => compareAsc(p.fromTime, p.toTime) === 0)
    ) {
      notify(
        t("presencePopup.startAndEndTimesForWorkTimeEntriesCannotBeEqual"),
        "warning",
        5000,
      );
    } else if (
      absenceHourRange &&
      formData.positions.some(
        (p) =>
          !areTimeRangesNonOverlapping([
            [absenceHourRange.from, absenceHourRange.to],
            [p.fromTime, p.toTime],
          ]),
      )
    ) {
      notify(
        t("presencePopup.positionOverlapsWithAbsence", {
          range: absenceHourRange.label,
        }),
        "warning",
        5000,
      );
    } else if (
      !areTimeRangesNonOverlapping(
        formData.positions.map((p) => [p.fromTime, p.toTime]),
      )
    ) {
      notify(
        t("presencePopup.startAndEndTimesForWorkTimeEntriesCannotBeTheSame"),
        "warning",
        5000,
      );
    } else {
      setIsLoading(true);
      try {
        const warning = await createOrUpdatePresence({
          employees: formData.employeeIds.map((employee) => {
            const [companyId, employeeId] = employee.split("-").map(Number);
            return {
              companyId,
              employeeId,
            };
          }),
          fromDate: format(formData.fromDate, "yyyy-MM-dd"),
          toDate: format(formData.toDate, "yyyy-MM-dd"),
          positions: formData.positions.map((position) => {
            const projectId = Number(position.projectId);
            const name = availableProjects.find(
              (p) => p.projectId === projectId,
            )?.name;
            return {
              fromHour: format(position.fromTime, "HH:mm:ss"),
              toHour: format(position.toTime, "HH:mm:ss"),
              project: {
                id: projectId,
                name: name ?? "",
              },
              note: position.note,
            };
          }),
          skipWeekendsAndHolidays: formData.skipWeekendsAndHolidays,
        });
        if (warning) {
          notify(warning, "warning", 8000);
        }
        onClose(true);
      } catch (error) {
        setIsLoading(false);
        notify(
          error instanceof Error
            ? error.message
            : t("common.unknownErrorOccurred"),
          "error",
          5000,
        );
      }
    }
  }, [formData, availableProjects, absenceHourRange, t, onClose]);

  // Zapis edycji istniejących pozycji (tylko modyfikowalne wg macierzy). Każda zmieniona pozycja
  // idzie osobnym PUT /api/presence-position/{id} - nie rusza pozostałych pozycji dnia.
  const handleSaveEdits = useCallback(async () => {
    const editablePositions = formData.positions.filter(
      (p) => p.id !== null && p.editable && p.original !== null,
    );
    const changed = editablePositions.filter(
      (p) =>
        p.original !== null &&
        (compareAsc(p.fromTime, p.original.fromTime) !== 0 ||
          compareAsc(p.toTime, p.original.toTime) !== 0 ||
          p.projectId !== p.original.projectId ||
          p.note !== p.original.note),
    );

    if (changed.length === 0) {
      notify(t("presencePopup.noChangesToSave"), "warning", 4000);
      return;
    }
    if (changed.some((p) => p.projectId === "")) {
      notify(
        t("presencePopup.eachItemMustHaveProjectSelected"),
        "warning",
        5000,
      );
      return;
    }
    if (changed.some((p) => compareAsc(p.fromTime, p.toTime) === 0)) {
      notify(
        t("presencePopup.startAndEndTimesForWorkTimeEntriesCannotBeEqual"),
        "warning",
        5000,
      );
      return;
    }
    // brak kolizji między wszystkimi pozycjami dnia (edytowane + pozostałe)
    if (
      !areTimeRangesNonOverlapping(
        formData.positions.map((p) => [p.fromTime, p.toTime]),
      )
    ) {
      notify(
        t("presencePopup.startAndEndTimesForWorkTimeEntriesCannotBeTheSame"),
        "warning",
        5000,
      );
      return;
    }
    if (
      absenceHourRange &&
      changed.some(
        (p) =>
          !areTimeRangesNonOverlapping([
            [absenceHourRange.from, absenceHourRange.to],
            [p.fromTime, p.toTime],
          ]),
      )
    ) {
      notify(
        t("presencePopup.positionOverlapsWithAbsence", {
          range: absenceHourRange.label,
        }),
        "warning",
        5000,
      );
      return;
    }

    setIsLoading(true);
    try {
      let lastWarning: string | undefined;
      for (const p of changed) {
        const projectId = Number(p.projectId);
        const name = availableProjects.find(
          (pr) => pr.projectId === projectId,
        )?.name;
        const warning = await updatePresencePosition(p.id as number, {
          date: format(date, "yyyy-MM-dd"),
          from: format(p.fromTime, "HH:mm:ss"),
          to: format(p.toTime, "HH:mm:ss"),
          projectId,
          project: name ?? "",
          note: p.note,
        });
        if (warning) lastWarning = warning;
      }
      if (lastWarning) notify(lastWarning, "warning", 8000);
      onClose(true);
    } catch (error) {
      setIsLoading(false);
      notify(
        error instanceof Error
          ? error.message
          : t("common.unknownErrorOccurred"),
        "error",
        5000,
      );
    }
  }, [formData.positions, date, absenceHourRange, availableProjects, t, onClose]);

  const hasEditablePositions = formData.positions.some(
    (p) => p.id !== null && p.editable,
  );

  const seedNewPosition = useCallback(() => {
    const fromTime = set(date, {
      hours: 7,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    });
    const toTime = set(date, {
      hours: 15,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    });
    return { fromTime, toTime, projectId: "", note: "" };
  }, [date]);

  const handleStartAdd = useCallback(() => {
    setNewPositions([seedNewPosition()]);
    setAddMode(true);
  }, [seedNewPosition]);

  const handleCancelAdd = useCallback(() => {
    setAddMode(false);
    setNewPositions([]);
  }, []);

  const handleAddNewRow = useCallback(() => {
    setNewPositions((prev) => {
      const last = prev[prev.length - 1];
      const start = last ? last.toTime : seedNewPosition().fromTime;
      const end = add(start, { hours: 1 });
      return [...prev, { fromTime: start, toTime: end, projectId: "", note: "" }];
    });
  }, [seedNewPosition]);

  const handleRemoveNewRow = useCallback((index: number) => {
    setNewPositions((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmitNewPositions = useCallback(async () => {
    if (newPositions.length === 0) {
      notify(t("presencePopup.presenceMustHaveAtLeastOneItem"), "warning", 5000);
      return;
    }
    if (newPositions.some((p) => p.projectId === "")) {
      notify(
        t("presencePopup.eachItemMustHaveProjectSelected"),
        "warning",
        5000,
      );
      return;
    }
    if (newPositions.some((p) => compareAsc(p.fromTime, p.toTime) === 0)) {
      notify(
        t("presencePopup.startAndEndTimesForWorkTimeEntriesCannotBeEqual"),
        "warning",
        5000,
      );
      return;
    }
    if (
      !areTimeRangesNonOverlapping(
        newPositions.map((p) => [p.fromTime, p.toTime]),
      )
    ) {
      notify(
        t("presencePopup.startAndEndTimesForWorkTimeEntriesCannotBeTheSame"),
        "warning",
        5000,
      );
      return;
    }
    // brak kolizji z istniejącymi pozycjami dnia
    const existingRanges: [Date, Date][] =
      entry.presence?.entrances.map((e) => [
        parse(e.from, "HH:mm:ss", date),
        parse(e.to, "HH:mm:ss", date),
      ]) ?? [];
    const overlapsExisting = newPositions.some(
      (p) =>
        !areTimeRangesNonOverlapping([
          ...existingRanges,
          [p.fromTime, p.toTime],
        ]),
    );
    if (overlapsExisting) {
      notify(
        t("presencePopup.startAndEndTimesForWorkTimeEntriesCannotBeTheSame"),
        "warning",
        5000,
      );
      return;
    }
    if (
      absenceHourRange &&
      newPositions.some(
        (p) =>
          !areTimeRangesNonOverlapping([
            [absenceHourRange.from, absenceHourRange.to],
            [p.fromTime, p.toTime],
          ]),
      )
    ) {
      notify(
        t("presencePopup.positionOverlapsWithAbsence", {
          range: absenceHourRange.label,
        }),
        "warning",
        5000,
      );
      return;
    }

    setIsLoading(true);
    try {
      let lastWarning: string | undefined;
      for (const p of newPositions) {
        const projectId = Number(p.projectId);
        const name = availableProjects.find(
          (pr) => pr.projectId === projectId,
        )?.name;
        const warning = await createPresencePosition({
          companyId,
          employeeId,
          date: format(date, "yyyy-MM-dd"),
          from: format(p.fromTime, "HH:mm:ss"),
          to: format(p.toTime, "HH:mm:ss"),
          projectId,
          project: name ?? "",
          note: p.note,
        });
        if (warning) lastWarning = warning;
      }
      if (lastWarning) notify(lastWarning, "warning", 8000);
      onClose(true);
    } catch (error) {
      setIsLoading(false);
      notify(
        error instanceof Error
          ? error.message
          : t("common.unknownErrorOccurred"),
        "error",
        5000,
      );
    }
  }, [
    newPositions,
    entry.presence,
    date,
    absenceHourRange,
    availableProjects,
    companyId,
    employeeId,
    t,
    onClose,
  ]);

  const [showDeleteCalendarPopup, setShowDeleteCalendarPopup] = useState(false);
  const [selectedDeleteDates, setSelectedDeleteDates] = useState<Date[]>([]);

  const deletableEntriesByDate = useMemo(() => {
    const map = new Map<string, number[]>();
    const source = employeeEntries ?? (entry.presence ? [entry] : []);
    for (const e of source) {
      if (!e.presence) continue;
      // Confirmed === null → dodane w Optimie, nie do edycji z naszej strony
      if (e.presence.confirmed === null) continue;
      // Confirmed === 2 → zatwierdzone przez kadry/admina; tylko HR/admin może je cofnąć i tylko jeśli jeszcze niezsynchronizowane
      if (e.presence.confirmed === 2) {
        if (!canDeleteFullyConfirmed) continue;
        if (e.presence.synchronized !== false) continue;
      }
      // tylko pozycje, które bieżący użytkownik może usunąć (własne dla kierownika/majstra,
      // dowolne dla kadr+) - kierownik nie skasuje wpisu innego kierownika nawet zbiorczo.
      const ids = e.presence.entrances
        .filter((en) => canModifyEntrance(en))
        .map((en) => en.id)
        .filter((id): id is number => id !== null && id !== undefined);
      if (ids.length > 0) {
        map.set(e.date, ids);
      }
    }
    return map;
  }, [employeeEntries, entry, canDeleteFullyConfirmed, canModifyEntrance]);

  const handleDelete = useCallback(async () => {
    if (!entry.presence) return;

    const confirmed = await confirm(
      t("presencePopup.confirmDeletePresence"),
      t("common.confirmation"),
    );
    if (!confirmed) return;

    const initial = deletableEntriesByDate.has(entry.date)
      ? [parse(entry.date, "yyyy-MM-dd", new Date())]
      : [];
    setSelectedDeleteDates(initial);
    setShowDeleteCalendarPopup(true);
  }, [entry.presence, entry.date, deletableEntriesByDate, t]);

  const handleDeleteConfirm = useCallback(async () => {
    if (selectedDeleteDates.length === 0) {
      notify(t("presencePopup.noDaysSelected"), "warning", 3000);
      return;
    }

    const ids: number[] = [];
    for (const d of selectedDeleteDates) {
      const key = format(d, "yyyy-MM-dd");
      const dayIds = deletableEntriesByDate.get(key);
      if (dayIds) ids.push(...dayIds);
    }

    if (ids.length === 0) {
      notify(t("common.unknownErrorOccurred"), "error", 5000);
      return;
    }

    setIsLoading(true);
    try {
      const warning = await rejectPresencePositions(ids);
      if (warning) {
        notify(warning, "warning", 8000);
      }
      onClose(true);
    } catch (error) {
      setIsLoading(false);
      setShowDeleteCalendarPopup(false);
      notify(
        error instanceof Error
          ? error.message
          : t("common.unknownErrorOccurred"),
        "error",
        5000,
      );
    }
  }, [selectedDeleteDates, deletableEntriesByDate, t, onClose]);

  const calendarDisabledDates = useCallback(
    (args: { date: Date; view: string }) => {
      if (args.view !== "month") return false;
      return !deletableEntriesByDate.has(format(args.date, "yyyy-MM-dd"));
    },
    [deletableEntriesByDate],
  );

  const handleSelectAllDeletable = useCallback(() => {
    const all = Array.from(deletableEntriesByDate.keys()).map((d) =>
      parse(d, "yyyy-MM-dd", new Date()),
    );
    setSelectedDeleteDates(all);
  }, [deletableEntriesByDate]);

  const handleClearSelection = useCallback(() => {
    setSelectedDeleteDates([]);
  }, []);

  useEffect(() => {
    if (formData.fromDate && formData.toDate) {
      const compareResult = compareDesc(formData.fromDate, formData.toDate);
      if (compareResult === 0) {
        let isWeekendDay = false;
        if (
          formData.fromDate.getDay() === 6 ||
          formData.fromDate.getDay() === 0
        ) {
          isWeekendDay = true;
        }

        setOnlyWeekendDays(formData.skipWeekendsAndHolidays && isWeekendDay);
      } else if (compareResult === 1) {
        const dates = eachDayOfInterval({
          start: formData.fromDate,
          end: formData.toDate,
        });

        let weekendDays = 0;
        for (const date of dates) {
          if (date.getDay() === 6 || date.getDay() === 0) {
            weekendDays++;
          }
        }

        // console.log({ dl: dates.length });
        // console.log({ dates });
        // console.log({ weekendDays });
        // console.log({ x: dates.length === weekendDays });

        setOnlyWeekendDays(
          formData.skipWeekendsAndHolidays && dates.length === weekendDays,
        );
      } else {
        setOnlyWeekendDays(false);
      }
    }
  }, [formData]);

  return (
    <>
    <Popup
      visible
      onHiding={() => onClose(false)}
      fullScreen={isMobile}
      maxWidth={isMobile ? undefined : 1000}
      height={isMobile ? "100%" : "auto"}
      maxHeight={isMobile ? undefined : "80vh"}
      title={t("presencePopup.sendPresence")}
      showCloseButton
      dragEnabled={!isMobile}
    >
      {availableEmployees.length === 0 ? (
        t("presencePopup.noEmployeesToSelect")
      ) : availableProjects.length === 0 ? (
        t("presencePopup.noProjectsToSelect")
      ) : (
        <div>
          {isReadOnly ? (
            <div className="p-4 mb-3 rounded-md border border-blue-500 bg-blue-50 text-blue-800 text-sm text-justify">
              {canAddToExistingPresence
                ? t("presencePopup.existingReadOnlyCanAddHint")
                : dayHasSyncedPosition
                  ? t("presencePopup.dayAlreadySyncedCannotAdd")
                  : t("presencePopup.readOnlyAlert")}
            </div>
          ) : null}
          {onlyWeekendDays ? (
            <div className="p-4 mb-3 rounded-md border-yellow-500 bg-yellow-50 text-yellow-800 text-sm text-justify">
              {t("presencePopup.onlyWeekendDaysAlert")}
            </div>
          ) : null}
          {absenceHourRange ? (
            <div className="p-4 mb-3 rounded-md border border-blue-500 bg-blue-50 text-blue-800 text-sm text-justify">
              <i className="dx-icon-info mr-2"></i>
              {t("presencePopup.absenceHourRangeInfo", {
                range: absenceHourRange.label,
                acronym: absenceHourRange.acronym,
              })}
            </div>
          ) : null}
          <div className="flex flex-col md:flex-row gap-6">
            <div className="basis-1/2 flex flex-col gap-4">
              <TagBox
                dataSource={availableEmployees}
                valueExpr={(item: PresenceAvailableEmployee) =>
                  !item ? "" : `${item.companyId}-${item.employeeId}`
                }
                displayExpr={(item: PresenceAvailableEmployee) =>
                  !item ? "" : `${item.lastName} ${item.firstName}`
                }
                labelMode="static"
                label={t("availabilityPopup.employee")}
                value={formData.employeeIds}
                onValueChange={(value) =>
                  setFormData(
                    produce(formData, (draft) => {
                      draft.employeeIds = value;
                    }),
                  )
                }
                searchEnabled
                hideSelectedItems
                disabled={isLoading || isReadOnly}
              />
              <div
                className={clsx(
                  "flex gap-4",
                  isMobile ? "flex-col" : "flex-row",
                )}
              >
                <DateBox
                  className={isMobile ? "" : "basis-1/2"}
                  labelMode="static"
                  label={t("presencePopup.startDate")}
                  min={minDate}
                  max={maxDate}
                  value={formData.fromDate}
                  pickerType={isMobile ? "rollers" : "calendar"}
                  onValueChange={(value) => {
                    if (value instanceof Date) {
                      setFormData(
                        produce(formData, (draft) => {
                          draft.fromDate = value;
                        }),
                      );
                    }
                  }}
                  disabled={isLoading || isReadOnly}
                />
                <DateBox
                  className={isMobile ? "" : "basis-1/2"}
                  labelMode="static"
                  label={t("presencePopup.endDate")}
                  min={minDate}
                  max={maxDate}
                  value={formData.toDate}
                  pickerType={isMobile ? "rollers" : "calendar"}
                  onValueChange={(value) => {
                    if (value instanceof Date) {
                      setFormData(
                        produce(formData, (draft) => {
                          draft.toDate = value;
                        }),
                      );
                    }
                  }}
                  disabled={isLoading || isReadOnly}
                />
              </div>
              <div>
                <CheckBox
                  text={t("presencePopup.skipDaysOff")}
                  value={formData.skipWeekendsAndHolidays}
                  onValueChange={(value) => {
                    if (typeof value === "boolean") {
                      setFormData(
                        produce(formData, (draft) => {
                          draft.skipWeekendsAndHolidays = value;
                        }),
                      );
                    }
                  }}
                  disabled={isLoading || isReadOnly}
                />
              </div>
            </div>
            <div className="basis-1/2">
              <div className="flex flex-col gap-4">
                <div
                  className={clsx(
                    "flex items-center gap-2",
                    isMobile ? "flex-col items-stretch" : "justify-between",
                  )}
                >
                  <div className="text-base font-semibold">
                    {t("presencePopup.workTime")}
                  </div>
                  {!isReadOnly && formData.positions.length < 10 ? (
                    <Button
                      stylingMode="outlined"
                      type="default"
                      icon="plus"
                      text={t("presencePopup.addWorkTimePosition")}
                      onClick={handleAddPositionClick}
                      disabled={isLoading}
                      width={isMobile ? "100%" : undefined}
                    />
                  ) : null}
                </div>
                {formData.positions.map((_, index) => (
                  <div
                    key={index}
                    className="p-4 bg-neutral-100 dark:bg-neutral-900"
                  >
                    <div className="flex flex-col gap-4">
                      <div className="flex gap-4">
                        <DateBox
                          className="basis-1/2"
                          type="time"
                          useMaskBehavior
                          pickerType={isMobile ? "rollers" : "list"}
                          interval={30}
                          labelMode="static"
                          label={t("presencePopup.startTime")}
                          value={formData.positions[index].fromTime}
                          onValueChange={(value) => {
                            if (value instanceof Date) {
                              setFormData(
                                produce(formData, (draft) => {
                                  draft.positions[index].fromTime = value;
                                }),
                              );
                            }
                          }}
                          disabled={
                            isLoading ||
                            (isReadOnly && !formData.positions[index].editable)
                          }
                        />
                        <DateBox
                          className="basis-1/2"
                          type="time"
                          useMaskBehavior
                          pickerType={isMobile ? "rollers" : "list"}
                          interval={30}
                          labelMode="static"
                          label={t("presencePopup.endTime")}
                          value={formData.positions[index].toTime}
                          onValueChange={(value) => {
                            if (value instanceof Date) {
                              setFormData(
                                produce(formData, (draft) => {
                                  draft.positions[index].toTime = value;
                                }),
                              );
                            }
                          }}
                          disabled={
                            isLoading ||
                            (isReadOnly && !formData.positions[index].editable)
                          }
                        />
                      </div>
                      <SelectBox
                        dataSource={availableProjects}
                        valueExpr={(item: PresenceAvailableProject) =>
                          !item ? "" : item.projectId.toString()
                        }
                        displayExpr={(item: PresenceAvailableProject) =>
                          !item ? "" : item.name
                        }
                        value={formData.positions[index].projectId}
                        onValueChange={(value) => {
                          setFormData(
                            produce(formData, (draft) => {
                              draft.positions[index].projectId = value;
                            }),
                          );
                        }}
                        searchEnabled
                        labelMode="static"
                        label={t("presencePopup.project")}
                        disabled={
                          isLoading ||
                          (isReadOnly && !formData.positions[index].editable)
                        }
                      />
                      <TextArea
                        labelMode="static"
                        label={t("presencePopup.notes")}
                        value={formData.positions[index].note}
                        onValueChange={(value) => {
                          setFormData(
                            produce(formData, (draft) => {
                              draft.positions[index].note = value;
                            }),
                          );
                        }}
                        disabled={
                          isLoading ||
                          (isReadOnly && !formData.positions[index].editable)
                        }
                      />
                      {!isReadOnly && formData.positions.length > 1 ? (
                        <Button
                          stylingMode="outlined"
                          type="danger"
                          icon="trash"
                          text={t("presencePopup.removeWorkTimePosition")}
                          disabled={isLoading}
                          width={isMobile ? "100%" : undefined}
                          onClick={() => {
                            confirm(
                              t(
                                "presencePopup.areYouSureYouWantToDeleteSelectedPosition",
                              ),
                              t("common.confirmation"),
                            ).then((value) => {
                              if (value === true) {
                                setFormData((formData) =>
                                  produce(formData, (draft) => {
                                    draft.positions.splice(index, 1);
                                  }),
                                );
                              }
                            });
                          }}
                        />
                      ) : null}
                    </div>
                  </div>
                ))}
                {!isReadOnly &&
                formData.positions.length > 2 &&
                formData.positions.length < 10 ? (
                  <Button
                    stylingMode="outlined"
                    type="default"
                    icon="plus"
                    text={t("presencePopup.addWorkTimePosition")}
                    onClick={handleAddPositionClick}
                    disabled={isLoading}
                    width={isMobile ? "100%" : undefined}
                  />
                ) : null}
              </div>
            </div>
          </div>
          {isReadOnly && canAddToExistingPresence ? (
            <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
              <div
                className={clsx(
                  "flex items-center gap-2",
                  isMobile ? "flex-col items-stretch" : "justify-between",
                )}
              >
                <div className="text-base font-semibold">
                  {t("presencePopup.addNewPositions")}
                </div>
                {!addMode ? (
                  <Button
                    stylingMode="contained"
                    type="default"
                    icon="plus"
                    text={t("presencePopup.addWorkTimePosition")}
                    onClick={handleStartAdd}
                    disabled={isLoading}
                    width={isMobile ? "100%" : undefined}
                  />
                ) : null}
              </div>

              {addMode ? (
                <div className="flex flex-col gap-4 mt-4">
                  {newPositions.map((_, index) => (
                    <div
                      key={index}
                      className="p-4 bg-neutral-100 dark:bg-neutral-900"
                    >
                      <div className="flex flex-col gap-4">
                        <div className="flex gap-4">
                          <DateBox
                            className="basis-1/2"
                            type="time"
                            useMaskBehavior
                            pickerType={isMobile ? "rollers" : "list"}
                            interval={30}
                            labelMode="static"
                            label={t("presencePopup.startTime")}
                            value={newPositions[index].fromTime}
                            onValueChange={(value) => {
                              if (value instanceof Date) {
                                setNewPositions((prev) =>
                                  produce(prev, (draft) => {
                                    draft[index].fromTime = value;
                                  }),
                                );
                              }
                            }}
                            disabled={isLoading}
                          />
                          <DateBox
                            className="basis-1/2"
                            type="time"
                            useMaskBehavior
                            pickerType={isMobile ? "rollers" : "list"}
                            interval={30}
                            labelMode="static"
                            label={t("presencePopup.endTime")}
                            value={newPositions[index].toTime}
                            onValueChange={(value) => {
                              if (value instanceof Date) {
                                setNewPositions((prev) =>
                                  produce(prev, (draft) => {
                                    draft[index].toTime = value;
                                  }),
                                );
                              }
                            }}
                            disabled={isLoading}
                          />
                        </div>
                        <SelectBox
                          dataSource={availableProjects}
                          valueExpr={(item: PresenceAvailableProject) =>
                            !item ? "" : item.projectId.toString()
                          }
                          displayExpr={(item: PresenceAvailableProject) =>
                            !item ? "" : item.name
                          }
                          value={newPositions[index].projectId}
                          onValueChange={(value) => {
                            setNewPositions((prev) =>
                              produce(prev, (draft) => {
                                draft[index].projectId = value;
                              }),
                            );
                          }}
                          searchEnabled
                          labelMode="static"
                          label={t("presencePopup.project")}
                          disabled={isLoading}
                        />
                        <TextArea
                          labelMode="static"
                          label={t("presencePopup.notes")}
                          value={newPositions[index].note}
                          onValueChange={(value) => {
                            setNewPositions((prev) =>
                              produce(prev, (draft) => {
                                draft[index].note = value;
                              }),
                            );
                          }}
                          disabled={isLoading}
                        />
                        {newPositions.length > 1 ? (
                          <Button
                            stylingMode="outlined"
                            type="danger"
                            icon="trash"
                            text={t("presencePopup.removeWorkTimePosition")}
                            disabled={isLoading}
                            width={isMobile ? "100%" : undefined}
                            onClick={() => handleRemoveNewRow(index)}
                          />
                        ) : null}
                      </div>
                    </div>
                  ))}

                  <div
                    className={clsx(
                      "flex gap-2",
                      isMobile ? "flex-col" : "flex-row flex-wrap",
                    )}
                  >
                    {newPositions.length < 10 ? (
                      <Button
                        stylingMode="outlined"
                        type="default"
                        icon="plus"
                        text={t("presencePopup.addWorkTimePosition")}
                        onClick={handleAddNewRow}
                        disabled={isLoading}
                        width={isMobile ? "100%" : undefined}
                      />
                    ) : null}
                    <Button
                      stylingMode="contained"
                      type="success"
                      icon="save"
                      text={
                        isLoading
                          ? t("common.sending")
                          : t("presencePopup.saveNewPositions")
                      }
                      onClick={handleSubmitNewPositions}
                      disabled={isLoading}
                      width={isMobile ? "100%" : undefined}
                    />
                    <Button
                      stylingMode="outlined"
                      type="normal"
                      text={t("common.cancel")}
                      onClick={handleCancelAdd}
                      disabled={isLoading}
                      width={isMobile ? "100%" : undefined}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
      {isReadOnly && hasEditablePositions ? (
        <ToolbarItem
          toolbar="bottom"
          location="after"
          widget="dxButton"
          options={
            {
              stylingMode: "contained",
              type: "success",
              icon: "save",
              text: isLoading
                ? t("common.sending")
                : t("presencePopup.saveChanges"),
              onClick: handleSaveEdits,
              disabled: isLoading,
            } as IButtonOptions
          }
        />
      ) : null}
      {isReadOnly && deletableEntriesByDate.size > 0 ? (
        <ToolbarItem
          toolbar="bottom"
          location="after"
          widget="dxButton"
          options={
            {
              stylingMode: "contained",
              type: "danger",
              icon: "trash",
              text: t("presencePopup.deletePresence"),
              onClick: handleDelete,
              disabled: isLoading,
            } as IButtonOptions
          }
        />
      ) : null}
      {!isReadOnly &&
      availableEmployees.length > 0 &&
      availableProjects.length > 0 ? (
        <ToolbarItem
          toolbar="bottom"
          location="after"
          widget="dxButton"
          options={
            {
              stylingMode: "contained",
              type: "default",
              text: isLoading ? t("common.sending") : t("common.send"),
              onClick: handleSubmit,
              disabled: isLoading,
            } as IButtonOptions
          }
          disabled={onlyWeekendDays}
        />
      ) : null}
    </Popup>
    {showDeleteCalendarPopup ? (
      <Popup
        visible
        onHiding={() => {
          if (!isLoading) setShowDeleteCalendarPopup(false);
        }}
        fullScreen={isMobile}
        maxWidth={isMobile ? undefined : 520}
        width={isMobile ? "100%" : undefined}
        height={isMobile ? "100%" : "auto"}
        maxHeight={isMobile ? undefined : "85vh"}
        title={t("presencePopup.deleteCalendarTitle")}
        showCloseButton
        dragEnabled={!isMobile}
      >
        <div className="flex flex-col gap-3 h-full">
          <div className="text-sm text-justify">
            {t("presencePopup.deleteCalendarHint")}
          </div>
          <div
            className={clsx(
              "flex gap-2",
              isMobile ? "flex-col" : "flex-row flex-wrap",
            )}
          >
            <Button
              stylingMode="outlined"
              type="default"
              text={t("presencePopup.selectAllDeletable")}
              onClick={handleSelectAllDeletable}
              disabled={isLoading || deletableEntriesByDate.size === 0}
              width={isMobile ? "100%" : undefined}
            />
            <Button
              stylingMode="outlined"
              type="normal"
              text={t("presencePopup.clearSelection")}
              onClick={handleClearSelection}
              disabled={isLoading || selectedDeleteDates.length === 0}
              width={isMobile ? "100%" : undefined}
            />
          </div>
          <div className="flex justify-center">
          <Calendar
            value={selectedDeleteDates}
            selectionMode="multiple"
            min={minDate}
            max={maxDate}
            disabledDates={calendarDisabledDates}
            width={isMobile ? "100%" : 480}
            onValueChange={(value) => {
              const toDate = (v: Date | number | string): Date | null => {
                if (v instanceof Date) return v;
                if (typeof v === "number") return new Date(v);
                if (typeof v === "string") {
                  const d = new Date(v);
                  return isNaN(d.getTime()) ? null : d;
                }
                return null;
              };
              if (Array.isArray(value)) {
                const dates = value
                  .map(toDate)
                  .filter((d): d is Date => d !== null);
                setSelectedDeleteDates(dates);
              } else if (value) {
                const d = toDate(value);
                setSelectedDeleteDates(d ? [d] : []);
              } else {
                setSelectedDeleteDates([]);
              }
            }}
            disabled={isLoading}
          />
          </div>
          <div className="text-sm font-medium">
            {t("presencePopup.selectedDaysCount", {
              count: selectedDeleteDates.length,
            })}
          </div>
        </div>
        <ToolbarItem
          toolbar="bottom"
          location={isMobile ? "center" : "after"}
          widget="dxButton"
          options={
            {
              stylingMode: "contained",
              type: "danger",
              icon: "trash",
              text: isLoading
                ? t("common.processingRequest")
                : t("presencePopup.deleteSelected"),
              onClick: handleDeleteConfirm,
              disabled: isLoading || selectedDeleteDates.length === 0,
              width: isMobile ? "100%" : undefined,
            } as IButtonOptions
          }
        />
        <ToolbarItem
          toolbar="bottom"
          location={isMobile ? "center" : "after"}
          widget="dxButton"
          options={
            {
              stylingMode: "outlined",
              type: "normal",
              text: t("common.cancel"),
              onClick: () => setShowDeleteCalendarPopup(false),
              disabled: isLoading,
              width: isMobile ? "100%" : undefined,
            } as IButtonOptions
          }
        />
      </Popup>
    ) : null}
    </>
  );
}
